import { useEffect, useRef, useState } from "react";
import { Plus, Trash, FloppyDisk, X, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiErrorMessage } from "@/lib/api";
import { cleanLabel } from "./shared";

const sel = "h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm";
const blank = () => ({ key: Math.random().toString(36).slice(2), employee: "", work_date: "", location_count: 1, job_ids: [""], note: "", allow_duplicate: false, error: "", saved: false });
const newKey = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function rowError(r) {
  if (!r.employee) return "Pick an employee";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.work_date)) return "Enter the work date";
  if (r.job_ids.slice(0, r.location_count).some((j) => !j)) return "Pick every jobsite";
  return "";
}

function Row({ r, i, emps, jobs, maxJobs, set, remove }) {
  const setCount = (n) => set({ location_count: n, job_ids: Array.from({ length: n }, (_, k) => r.job_ids[k] || "") });
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${r.saved ? "border-emerald-300 bg-emerald-50/40" : r.error ? "border-rose-300" : "border-slate-200"}`} data-testid={`manual-row-${i}`}>
      <div className="grid grid-cols-2 md:grid-cols-[1fr_160px_120px_1.4fr_auto] gap-2 items-start">
        <select className={sel} value={r.employee} disabled={r.saved} onChange={(e) => set({ employee: e.target.value })} data-testid={`manual-employee-${i}`}>
          <option value="">Employee…</option>{emps.map((n) => <option key={n}>{n}</option>)}
        </select>
        <Input type="date" className="cal-white h-9" value={r.work_date} disabled={r.saved} onChange={(e) => set({ work_date: e.target.value })} data-testid={`manual-date-${i}`} />
        <select className={sel} value={r.location_count} disabled={r.saved} onChange={(e) => setCount(Number(e.target.value))} data-testid={`manual-count-${i}`} aria-label="Number of locations">
          {Array.from({ length: maxJobs + 1 }, (_, n) => <option key={n} value={n}>{n} location{n === 1 ? "" : "s"}</option>)}
        </select>
        <Input value={r.note} disabled={r.saved} onChange={(e) => set({ note: e.target.value })} placeholder="Reason / note (optional)" className="h-9" data-testid={`manual-note-${i}`} />
        <button type="button" onClick={remove} disabled={r.saved} className="h-9 w-9 inline-flex items-center justify-center text-rose-500 hover:text-rose-700 disabled:opacity-30" aria-label="Remove row" data-testid={`manual-remove-${i}`}><Trash size={16} /></button>
      </div>
      {r.location_count > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {r.job_ids.map((j, k) => (
            <select key={k} className={sel} value={j} disabled={r.saved} onChange={(e) => { const n = [...r.job_ids]; n[k] = e.target.value; set({ job_ids: n }); }} data-testid={`manual-job-${i}-${k + 1}`}>
              <option value="">Jobsite {k + 1}…</option>{jobs.map((x) => <option key={x.id} value={x.id}>{cleanLabel(x.label)}</option>)}
            </select>
          ))}
        </div>
      )}
      {r.error && !r.saved && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-rose-600" data-testid={`manual-error-${i}`}>
          <span className="inline-flex items-center gap-1"><Warning size={13} />{r.error === "duplicate" ? "A timesheet already exists for this employee and date." : r.error}</span>
          {r.error === "duplicate" && (
            <label className="inline-flex items-center gap-1 text-slate-600"><input type="checkbox" checked={r.allow_duplicate} onChange={(e) => set({ allow_duplicate: e.target.checked })} data-testid={`manual-allow-dup-${i}`} />Save anyway and flag for review</label>
          )}
        </div>
      )}
      {r.saved && <div className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle size={13} />Saved{r.flagged ? " · flagged as possible duplicate" : ""}</div>}
    </div>
  );
}

export default function ManualEntryPanel({ onClose, onSaved }) {
  const [rows, setRows] = useState([blank()]);
  const [opts, setOpts] = useState({ emps: [], jobs: [], max: 4 });
  const [busy, setBusy] = useState(false);
  const batch = useRef(newKey());

  useEffect(() => {
    Promise.all([api.get("/timesheet/employees"), api.get("/timesheet/jobs"), api.get("/public/timesheet/form")]).then(([e, j, f]) =>
      setOpts({ emps: e.data.items.filter((x) => x.active).map((x) => x.name), jobs: j.data.items.filter((x) => x.active), max: f.data.schema.max_jobs || 4 }))
      .catch((err) => toast.error(apiErrorMessage(err) || "Couldn't load employees/jobs"));
  }, []);

  const set = (i) => (patch) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch, error: patch.allow_duplicate !== undefined ? r.error : "" } : r)));
  const pending = rows.filter((r) => !r.saved);
  const save = async () => {
    const checked = rows.map((r) => (r.saved ? r : { ...r, error: rowError(r) }));
    setRows(checked);
    if (checked.some((r) => !r.saved && r.error)) return toast.error("Fix the highlighted rows first");
    const todo = checked.map((r, i) => ({ r, i })).filter(({ r }) => !r.saved);
    if (!todo.length || !window.confirm(`Save ${todo.length} manual timesheet entr${todo.length === 1 ? "y" : "ies"}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/timesheet/raw/manual", { batch_key: batch.current, rows: todo.map(({ r }) => ({ employee: r.employee, work_date: r.work_date, location_count: r.location_count, job_ids: r.job_ids, note: r.note, allow_duplicate: r.allow_duplicate })) });
      batch.current = newKey();
      const next = [...checked];
      data.results.forEach((res) => { const idx = todo[res.index].i; next[idx] = { ...next[idx], saved: res.ok, flagged: res.flagged, error: res.ok ? "" : res.error }; });
      setRows(next);
      const failed = data.results.filter((x) => !x.ok).length;
      if (data.saved) { toast.success(`${data.saved} entr${data.saved === 1 ? "y" : "ies"} saved as Manual`); onSaved(); }
      if (failed) toast.error(`${failed} row${failed === 1 ? "" : "s"} not saved — see the highlighted rows`);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border-2 border-[#f59e0b]/70 bg-[#f59e0b]/[0.04] p-4 space-y-3 mb-4" data-testid="manual-entry-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-[#d97706]">Manual Timesheet Entry</h3>
          <p className="text-xs text-slate-500">Fill in missed days. Saved entries are marked <b>Manual</b> with your name and count on the dashboard right away.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setRows((rs) => [...rs, blank()])} disabled={rows.length >= 50} data-testid="manual-add-row"><Plus size={14} className="mr-1" />Add Row</Button>
          <Button size="sm" onClick={save} disabled={busy || !pending.length} className="bg-[#f59e0b] hover:bg-[#d97706] text-white" data-testid="manual-save"><FloppyDisk size={14} className="mr-1" />{busy ? "Saving…" : "Save Entries"}</Button>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="manual-cancel"><X size={14} className="mr-1" />{pending.length ? "Cancel" : "Done"}</Button>
        </div>
      </div>
      {rows.map((r, i) => <Row key={r.key} r={r} i={i} emps={opts.emps} jobs={opts.jobs} maxJobs={opts.max} set={set(i)} remove={() => setRows((rs) => (rs.length > 1 ? rs.filter((_, k) => k !== i) : [blank()]))} />)}
    </div>
  );
}
